import * as React from 'react';
import {
  Button as ChakraButton,
  type ButtonProps as ChakraButtonProps,
  chakra,
  defineRecipe,
  type RecipeVariantProps,
} from '@chakra-ui/react';

/**
 * Project Button.
 *
 * Variants and base styles live here (not in `src/theme/chakra.ts`).
 * Mirrors the old NativeBase Button theme: pill shape (30px radius),
 * 700 weight, 48px min-height, secondary-500 solid fill.
 *
 * Variants: `solid` (default, secondary.500), `outline`, `link`,
 * `ghost` (transparent, consumer-styled), `plain` (unstyled escape
 * hatch), `fullWidth`.
 *
 * Sizes: `md` / `lg` (48px height, NB CTA parity), `sm`, `xs`.
 * `lg` is included because several onboarding Next buttons pass
 * `size="lg"`; without it they fell through to Chakra's 44px `lg`.
 *
 * NB `onPress` maps to standard `onClick`; NB `isLoading` maps to
 * Chakra's `loading`; migrate those at the call site.
 */
const buttonRecipe = defineRecipe({
  className: 'ayble-button',
  base: {
    width: 'auto',
    // Design system: CTAs are full pills (Figma uses a 100px radius on every
    // button) and SemiBold, not the 30px / 700 the NativeBase theme used.
    borderRadius: 'pill',
    fontWeight: '600',
    letterSpacing: '0.02em',
    _disabled: { opacity: 0.4 },
  },
  variants: {
    size: {
      // Explicit `h` overrides Chakra Button's built-in size heights
      // (`md` → 40px / `h:10`, `lg` → 44px / `h:11`). NB theme used
      // minHeight 48px across CTA sizes.
      md: {
        h: '48px',
        minH: '48px',
        py: '8px',
        px: '30px',
        fontSize: 'md',
      },
      lg: {
        h: '48px',
        minH: '48px',
        py: '8px',
        px: '30px',
        fontSize: 'md',
      },
      sm: {
        h: '32px',
        minH: '32px',
        py: '4px',
        px: '12px',
        fontSize: 'sm',
      },
      xs: {
        h: '20px',
        minH: '20px',
        py: '2px',
        px: '8px',
        fontSize: 'xs',
      },
    },
    variant: {
      solid: {
        bg: 'secondary.500',
        color: 'white',
        _hover: { bg: 'secondary.200' },
        _active: { bg: 'secondary.300' },
      },
      // Design system outline: a faint Evergreen wash rather than solid white,
      // with an Evergreen border and label (Figma "Button Outline CTA").
      outline: {
        bg: 'rgba(4, 51, 56, 0.05)',
        borderWidth: '1px',
        borderColor: 'secondary.500',
        color: 'secondary.500',
        _hover: { bg: 'rgba(4, 51, 56, 0.09)' },
        _active: {
          bg: 'secondary.500',
          borderColor: 'secondary.500',
          color: 'light.100',
        },
      },
      link: {
        bg: 'transparent',
        color: 'secondary.200',
        textDecoration: 'none',
        // Neutralise Chakra's built-in link/ghost hover bg so these stay
        // text-only; underline is the hover affordance.
        _hover: {
          bg: 'transparent',
          color: 'secondary.200',
          textDecoration: 'underline',
        },
        _active: { bg: 'transparent' },
      },
      ghost: {
        bg: 'transparent',
        borderWidth: 0,
        _hover: { bg: 'transparent' },
        _active: { bg: 'transparent' },
      },
      plain: {
        bg: 'transparent',
        borderWidth: 0,
        p: 0,
        minHeight: 'auto',
        _hover: { bg: 'transparent' },
        _active: { bg: 'transparent' },
      },
      fullWidth: {
        width: '90%',
      },
    },
  },
  defaultVariants: { variant: 'solid', size: 'md' },
});

const StyledButton = chakra(ChakraButton, buttonRecipe);

export type ButtonProps = Omit<ChakraButtonProps, 'variant' | 'size'> &
  RecipeVariantProps<typeof buttonRecipe>;

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (props, ref) => <StyledButton ref={ref} {...props} />
);
Button.displayName = 'Button';
